// Sezione a blocchi del configuratore: quick-add marca/modello + lista prodotti
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Plus } from 'lucide-react';
import { EXPAND_ANIMATION } from './constants';
import { BrandModelSelector } from './BrandModelSelector';
import { NumericStepper } from './NumericStepper';
import { CompactProductCard } from './CompactProductCard';

export function ProductSection({
  title, icon: Icon, colors, badgeClass, count,
  typeCode, typeCodes, items,
  onAdd, onUpdateQuantity, onRemove,
  saving, readOnly, emptyMessage, addTestId, keyPrefix,
  maxQty = 999, headerBadge = null, headerAction = null, collapsibleOpen,
}) {
  const [pendingItem, setPendingItem] = useState(null);
  const [pendingQty, setPendingQty] = useState(1);

  const handleAdd = () => {
    onAdd(pendingItem, pendingQty);
    setPendingItem(null);
    setPendingQty(1);
  };

  const content = (
    <CardContent className="p-6 space-y-4">
      {/* Quick Add */}
      {!readOnly && (
        <div className="flex flex-col sm:flex-row gap-3">
          <BrandModelSelector
            typeCode={typeCode}
            typeCodes={typeCodes}
            selectedItem={pendingItem}
            onSelect={setPendingItem}
          />
          <div className="flex items-center gap-2">
            <NumericStepper
              value={pendingQty}
              onChange={setPendingQty}
              min={1}
              max={maxQty}
              testId={`${keyPrefix}-stepper`}
            />
            <Button
              onClick={handleAdd}
              disabled={!pendingItem || saving}
              className="bg-zinc-900 hover:bg-zinc-800"
              data-testid={addTestId}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              <span className="ml-2 hidden sm:inline">Aggiungi</span>
            </Button>
          </div>
        </div>
      )}

      {/* Lista prodotti */}
      <AnimatePresence mode="popLayout">
        {items.length === 0 ? (
          <div className="text-center py-8 text-zinc-400 border border-dashed border-zinc-200 rounded-xl">
            <Icon className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{emptyMessage}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(({ item, originalIndex }) => (
              <CompactProductCard
                key={`${keyPrefix}-${originalIndex}`}
                item={item}
                quantity={item.quantity}
                onQuantityChange={(qty) => onUpdateQuantity(originalIndex, qty)}
                onRemove={() => onRemove(originalIndex)}
                readOnly={readOnly}
              />
            ))}
          </div>
        )}
      </AnimatePresence>
    </CardContent>
  );

  return (
    <Card className="bg-white border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
      <CardHeader className="border-b border-zinc-100">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold text-zinc-900 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${colors.bg} border ${colors.border} flex items-center justify-center`}>
              <Icon className={`h-5 w-5 ${colors.icon}`} />
            </div>
            {title}
            {headerBadge}
            {count > 0 && (
              <Badge className={badgeClass}>{count} pz</Badge>
            )}
          </CardTitle>
          {headerAction}
        </div>
      </CardHeader>
      {collapsibleOpen === undefined ? content : (
        <AnimatePresence>
          {collapsibleOpen && (
            <motion.div {...EXPAND_ANIMATION}>
              {content}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </Card>
  );
}
